import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { ArrowLeft, Save, UserPlus, User, Send } from 'lucide-react'
import { useAuth } from '../hooks/useAuth.jsx'
import toast from 'react-hot-toast'
import api from '../services/authService'
import empresasService from '../services/empresasService'
import FieldHelp from '../components/FieldHelp'
import { getNumerosValidos, resumenDestinatarios } from '../utils/numeros'

function confirmarCambioEstado(estadoOriginal, estadoNuevo) {
  if (!estadoOriginal || estadoOriginal === estadoNuevo) return true

  if (estadoOriginal === 'OK' && estadoNuevo === 'DEUDA') {
    if (!confirm('Va a pasar este cliente de OK a DEUDA. El bot le enviará WhatsApp en el próximo ciclo si sigue en DEUDA. ¿Continuar?')) {
      return false
    }
    return confirm('Confirmación extra: ¿está seguro de marcar DEUDA? Esta acción habilita el envío automático.')
  }

  if (estadoOriginal === 'DEUDA' && estadoNuevo === 'OK') {
    if (!confirm('Va a pasar este cliente de DEUDA a OK. Dejará de recibir cobranza automática. ¿Continuar?')) {
      return false
    }
    return confirm('Confirmación extra: si el cron de cobranza ya está recorriendo la lista, el envío se omite solo si el servicio ya revalidó el estado en vivo. ¿Confirma pasar a OK?')
  }

  return true
}

function ClienteForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { hasPermission, hasAnyPermission } = useAuth()
  const [loading, setLoading] = useState(false)
  const [enviandoPrueba, setEnviandoPrueba] = useState(false)
  const [cliente, setCliente] = useState(null)
  const [empresas, setEmpresas] = useState([])
  const isEditing = !!id
  const [tipoCliente, setTipoCliente] = useState('NORMAL')

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      estado: 'OK',
      tipo_cliente: 'NORMAL',
      dia_cobro: 13
    }
  })

  const numeroWatch = watch('numero', '')
  const preview = resumenDestinatarios(numeroWatch)

  useEffect(() => {
    cargarEmpresas()
    if (isEditing) {
      cargarCliente()
    } else if (location.state && location.state.clienteCopiado) {
      const data = location.state.clienteCopiado
      setValue('empresa', data.empresa || '')
      setValue('nombre', data.nombre || '')
      setValue('numero', data.numero || '')
      setValue('estado', data.estado || 'OK')
      setValue('placa', data.placa || '')
      setValue('mensaje', data.mensaje || '')
      setValue('desde_mesanio_deuda', data.desde_mesanio_deuda || '')
      setValue('dia_cobro', data.dia_cobro ?? 13)
      setValue('tipo_cliente', data.tipo_cliente || 'NORMAL')
      setTipoCliente(data.tipo_cliente || 'NORMAL')
      setValue('fecha_hora_envio', data.fecha_hora_envio ? data.fecha_hora_envio.substring(0, 16) : '')
      setValue('repetir_cada_dias', data.repetir_cada_dias ?? 0)
    }
  }, [id, location.state])

  const cargarEmpresas = async () => {
    try {
      const response = await empresasService.getEmpresas({ combo: 1, activo: 1 })
      setEmpresas(response.data || [])
    } catch (error) {
      console.error('Error cargando empresas:', error)
      toast.error('No se pudieron cargar las empresas WhatsApp')
    }
  }

  const cargarCliente = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/clientes/${id}`)
      const clienteData = response.data.data
      setCliente(clienteData)

      setValue('empresa', clienteData.empresa)
      setValue('nombre', clienteData.nombre)
      setValue('numero', clienteData.numero)
      setValue('estado', clienteData.estado)
      setValue('placa', clienteData.placa)
      setValue('mensaje', clienteData.mensaje)
      setValue('desde_mesanio_deuda', clienteData.desde_mesanio_deuda || '')
      setValue('dia_cobro', clienteData.dia_cobro ?? 13)
      setValue('tipo_cliente', clienteData.tipo_cliente || 'NORMAL')
      setTipoCliente(clienteData.tipo_cliente || 'NORMAL')
      setValue('fecha_hora_envio', clienteData.fecha_hora_envio ? clienteData.fecha_hora_envio.substring(0, 16) : '')
      setValue('repetir_cada_dias', clienteData.repetir_cada_dias ?? 0)
    } catch (error) {
      console.error('Error cargando cliente:', error)
      toast.error('Error al cargar el cliente')
      navigate('/dashboard')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data) => {
    try {
      const validos = getNumerosValidos(data.numero)
      if (validos.length === 0) {
        toast.error('Ingrese al menos un número válido (8 a 15 dígitos, separados por coma)')
        return
      }

      if (!confirmarCambioEstado(cliente?.estado, data.estado)) {
        return
      }

      setLoading(true)
      data.tipo_cliente = tipoCliente
      data.numero = validos.join(',')
      if (!data.dia_cobro || data.dia_cobro === '') {
        data.dia_cobro = 13
      }
      if (tipoCliente !== 'PROGRAMADO') {
        delete data.fecha_hora_envio
        delete data.repetir_cada_dias
      }
      if (tipoCliente === 'PROGRAMADO') {
        if (!data.fecha_hora_envio) {
          toast.error('La fecha y hora de envío es requerida')
          setLoading(false)
          return
        }
        if (data.repetir_cada_dias === undefined || data.repetir_cada_dias === null || data.repetir_cada_dias === '') {
          toast.error('El campo repetir cada X días es requerido')
          setLoading(false)
          return
        }
      }

      if (isEditing) {
        await api.put(`/clientes/${id}`, data)
        toast.success('Cliente actualizado correctamente')
      } else {
        await api.post('/clientes', data)
        toast.success('Cliente creado correctamente')
      }

      navigate('/dashboard')
    } catch (error) {
      console.error('Error guardando cliente:', error)
      const errorMessage = error.response?.data?.message || 'Error al guardar el cliente'
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const enviarPrueba = async () => {
    if (!isEditing) return
    const n = preview.total
    if (n === 0) {
      toast.error('No hay números válidos. Guarde primero un número correcto.')
      return
    }
    if (!confirm(`Se enviará el mensaje guardado en BD a ${n} número(s) por la empresa ${cliente?.empresa || ''}. El estado del cliente NO cambia. ¿Continuar?`)) {
      return
    }
    try {
      setEnviandoPrueba(true)
      const response = await api.post(`/clientes/${id}/enviar-prueba`, {}, { timeout: 120000 })
      toast.success(response.data.message || 'Mensaje enviado')
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error al enviar el mensaje de prueba')
    } finally {
      setEnviandoPrueba(false)
    }
  }

  const empresasCombo = [...empresas]
  if (cliente?.empresa && !empresasCombo.some((e) => e.codigo === cliente.empresa)) {
    empresasCombo.unshift({ codigo: cliente.empresa, nombre: `${cliente.empresa} (registro actual)`, activo: 1 })
  }

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="btn btn-secondary"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Volver
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              {isEditing ? (
                <User className="h-6 w-6 text-primary-600" />
              ) : (
                <UserPlus className="h-6 w-6 text-success-600" />
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="tipo_cliente" className="form-label">
                    Tipo de Cliente *
                  </label>
                  <select
                    id="tipo_cliente"
                    {...register('tipo_cliente')}
                    value={tipoCliente}
                    onChange={e => { setTipoCliente(e.target.value); setValue('tipo_cliente', e.target.value) }}
                    className="form-select"
                  >
                    <option value="NORMAL">NORMAL</option>
                    <option value="PROGRAMADO">PROGRAMADO</option>
                  </select>
                  <FieldHelp>
                    NORMAL: entra a cobranza y al paso automático OK→DEUDA. PROGRAMADO: envía solo en la fecha/hora indicada.
                  </FieldHelp>
                </div>

                <div>
                  <label htmlFor="empresa" className="form-label">
                    Empresa / bot WhatsApp *
                  </label>
                  <select
                    id="empresa"
                    {...register('empresa', {
                      required: 'La empresa es requerida'
                    })}
                    className={`form-select ${errors.empresa ? 'border-red-500' : ''}`}
                  >
                    <option value="">Seleccionar empresa</option>
                    {empresasCombo.map((empresa) => (
                      <option key={empresa.codigo} value={empresa.codigo}>
                        {empresa.nombre} ({empresa.codigo})
                      </option>
                    ))}
                  </select>
                  <FieldHelp>
                    Elige el bot que enviará el mensaje. Se administra en Gestión de Empresas (token + instance id).
                  </FieldHelp>
                  {errors.empresa && (
                    <p className="mt-1 text-sm text-red-600">{errors.empresa.message}</p>
                  )}
                </div>

                {tipoCliente === 'PROGRAMADO' && (
                  <>
                    <div>
                      <label htmlFor="fecha_hora_envio" className="form-label">
                        Fecha y hora de envío *
                      </label>
                      <input
                        id="fecha_hora_envio"
                        type="datetime-local"
                        {...register('fecha_hora_envio')}
                        className="form-input"
                      />
                      <FieldHelp>El servicio revisa cada minuto y envía cuando coincide año, mes, día, hora y minuto.</FieldHelp>
                    </div>
                    <div>
                      <label htmlFor="repetir_cada_dias" className="form-label">
                        Repetir cada X días *
                      </label>
                      <input
                        id="repetir_cada_dias"
                        type="number"
                        min={0}
                        {...register('repetir_cada_dias', { valueAsNumber: true })}
                        className="form-input"
                        placeholder="0 = solo una vez"
                      />
                      <FieldHelp>0 = un solo envío. Si es mayor, después de enviar se programa la siguiente fecha.</FieldHelp>
                    </div>
                  </>
                )}

                <div>
                  <label htmlFor="nombre" className="form-label">Nombre *</label>
                  <input
                    id="nombre"
                    type="text"
                    {...register('nombre', { required: 'El nombre es requerido' })}
                    className={`form-input ${errors.nombre ? 'border-red-500' : ''}`}
                    placeholder="Nombre del cliente"
                  />
                  {errors.nombre && (
                    <p className="mt-1 text-sm text-red-600">{errors.nombre.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="numero" className="form-label">Número(s) WhatsApp *</label>
                  <input
                    id="numero"
                    type="text"
                    {...register('numero', { required: 'El número es requerido' })}
                    className={`form-input ${errors.numero ? 'border-red-500' : ''}`}
                    placeholder="51987654321,51912345678"
                  />
                  <FieldHelp>
                    Uno o más números separados por coma. Solo dígitos, con código de país (8 a 15 dígitos). Ejemplo: 51987654321,51912345678
                  </FieldHelp>
                  <p className={`mt-1 text-sm ${preview.total > 0 ? 'text-green-700' : 'text-red-600'}`}>
                    Este mensaje se enviará a <strong>{preview.total}</strong> número(s)
                    {preview.validos.length > 0 ? `: ${preview.validos.join(', ')}` : '.'}
                    {preview.invalidos.length > 0 && ` Inválidos: ${preview.invalidos.join(', ')}`}
                  </p>
                  {errors.numero && (
                    <p className="mt-1 text-sm text-red-600">{errors.numero.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="placa" className="form-label">Placa *</label>
                  <input
                    id="placa"
                    type="text"
                    {...register('placa', { required: 'La placa es requerida' })}
                    className={`form-input ${errors.placa ? 'border-red-500' : ''}`}
                    placeholder="Placa del vehículo"
                  />
                  {errors.placa && (
                    <p className="mt-1 text-sm text-red-600">{errors.placa.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="estado" className="form-label">Estado *</label>
                  <select
                    id="estado"
                    {...register('estado', { required: 'El estado es requerido' })}
                    className={`form-select ${errors.estado ? 'border-red-500' : ''}`}
                  >
                    <option value="">Seleccionar estado</option>
                    <option value="OK">OK</option>
                    <option value="DEUDA">DEUDA</option>
                    <option value="CANCELADO">CANCELADO</option>
                    <option value="MOROSO">MOROSO</option>
                    <option value="OTROS">OTROS</option>
                  </select>
                  <FieldHelp>
                    DEUDA: el bot envía WhatsApp en el ciclo de cobranza. OK: no cobra (puede pasar a DEUDA automáticamente según mes/día). CANCELADO, MOROSO y OTROS no se envían.
                  </FieldHelp>
                  {errors.estado && (
                    <p className="mt-1 text-sm text-red-600">{errors.estado.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="desde_mesanio_deuda" className="form-label">
                    Desde (Mes/Año)
                  </label>
                  <input
                    id="desde_mesanio_deuda"
                    type="text"
                    {...register('desde_mesanio_deuda')}
                    className="form-input"
                    placeholder="Ejemplo: ENERO2024"
                  />
                  <FieldHelp>Formato MESYYYY (ejemplo: ENERO2024). El bot usa este mes para pasar de OK a DEUDA.</FieldHelp>
                </div>

                <div>
                  <label htmlFor="dia_cobro" className="form-label">Día de Cobro</label>
                  <input
                    id="dia_cobro"
                    type="number"
                    min="1"
                    max="31"
                    {...register('dia_cobro', {
                      valueAsNumber: true,
                      min: { value: 1, message: 'El día debe ser entre 1 y 31' },
                      max: { value: 31, message: 'El día debe ser entre 1 y 31' }
                    })}
                    className={`form-input ${errors.dia_cobro ? 'border-red-500' : ''}`}
                    placeholder="13"
                    defaultValue={13}
                  />
                  <FieldHelp>Día del mes (1-31) para cambiar a DEUDA cuando ya pasó un mes. Por defecto: 13.</FieldHelp>
                  {errors.dia_cobro && (
                    <p className="mt-1 text-sm text-red-600">{errors.dia_cobro.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="mensaje" className="form-label">Mensaje *</label>
                <textarea
                  id="mensaje"
                  rows="6"
                  {...register('mensaje', { required: 'El mensaje es requerido' })}
                  className={`form-input ${errors.mensaje ? 'border-red-500' : ''}`}
                  placeholder="Mensaje personalizado para el cliente"
                />
                <FieldHelp>El mismo texto se envía a todos los números de este cliente.</FieldHelp>
                {errors.mensaje && (
                  <p className="mt-1 text-sm text-red-600">{errors.mensaje.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Cancelar
                </button>
                {isEditing && hasAnyPermission(['ENVIAR_MENSAJE_PRUEBA', 'EDITAR_CLIENTE']) && (
                  <button
                    type="button"
                    className="btn btn-warning"
                    disabled={enviandoPrueba || loading}
                    onClick={enviarPrueba}
                  >
                    <Send className="h-4 w-4 mr-2" />
                    {enviandoPrueba ? 'Enviando...' : 'Enviar mensaje'}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn btn-primary"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Guardando...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Save className="h-4 w-4 mr-2" />
                      {isEditing ? 'Actualizar' : 'Crear'} Cliente
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ClienteForm
